import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Star, Heart, Award, Users, Eye, Home, Hammer, TrendingUp, MapPin, X } from "lucide-react";

const Hero = ({ setActiveTab }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Enhanced gallery items for homepage preview - showcasing comprehensive portfolio
  const featuredGalleryItems = [
    { 
      id: 1, 
      type: "image", 
      src: "/image/0d5126ed-1e60-4b60-a586-00c2cd83f2db.jpg",
      title: "Modern Kitchen Renovation",
      category: "kitchen"
    },
    { 
      id: 2, 
      type: "image", 
      src: "/image/3b201da7-0207-48c3-bffd-ecb8635f7f1e.jpg",
      title: "Contemporary Living Space",
      category: "living"
    },
    { 
      id: 3, 
      type: "image", 
      src: "/image/555ba06c-7a90-4563-8f78-a6cff15a9ee7.jpg",
      title: "Luxury Bathroom Design",
      category: "bathroom"
    },
    { 
      id: 4, 
      type: "image", 
      src: "/image/270ffe8e-1727-4923-8ccb-a8e8e873163f.jpg",
      title: "Stunning Exterior",
      category: "exterior"
    },
    { 
      id: 5, 
      type: "image", 
      src: "/image/523ffe99-3025-4bc5-b382-aafcb294bc34.jpg",
      title: "Master Bedroom Suite",
      category: "bedroom"
    },
    { 
      id: 6, 
      type: "image", 
      src: "/image/73701879-b173-4d6b-9157-0df1ba1d3e5d.jpg",
      title: "Gourmet Kitchen",
      category: "kitchen"
    },
    { 
      id: 7, 
      type: "image", 
      src: "/image/2913649c-7dbc-4b5a-af4a-f47f93e30cdf.jpg",
      title: "Modern Interior Design",
      category: "interior"
    },
    { 
      id: 8, 
      type: "image", 
      src: "/image/8676f577-587c-4bad-bb19-a03d6fd1ec1d.jpg",
      title: "Outdoor Entertainment",
      category: "exterior"
    },
    { 
      id: 9, 
      type: "image", 
      src: "/image/8b2a655e-bcee-43b8-988b-c4aabdefbf81.jpg",
      title: "Elegant Living Room",
      category: "living"
    },
    { 
      id: 10, 
      type: "image", 
      src: "/image/92861d7f-b966-46ee-8895-01396e099bbc.jpg",
      title: "Modern Bathroom",
      category: "bathroom"
    }
  ];

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image using Hero1.jpeg as specified */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/image/Hero1.jpeg')`,
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/30 to-orange-900/30"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto text-center">
            <div className="animate-fade-in-up">
              <Badge className="mb-4 bg-amber-500/20 text-amber-100 border-amber-300 px-4 py-2 text-sm font-medium">
                San Diego Real Estate Developer
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Sddolphinflipper</span>
              </h1>
              
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Aaron Hands Real Estate Development
              </h2>
              
              <p className="text-lg md:text-xl text-gray-200 mb-6 max-w-2xl mx-auto leading-relaxed">
                Transforming San Diego properties since 2014. Multiple stunning remodels, creative designs, and over 50 local craftsmen employed in our journey of excellence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-3 text-base font-medium rounded-full shadow-2xl hover:scale-105 transition-all duration-300"
                  onClick={() => setActiveTab("gallery")}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Explore Portfolio
                </Button>
                
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-6 py-3 text-base font-medium rounded-full backdrop-blur-sm bg-white/10 hover:scale-105 transition-all duration-300"
                  onClick={() => setActiveTab("contact")}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Get In Touch
                </Button>
              </div>
              
              {/* Compact Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-amber-400 mb-1">24+</div>
                  <div className="text-gray-300 text-xs">Projects</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-orange-400 mb-1">11</div>
                  <div className="text-gray-300 text-xs">Years</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-red-400 mb-1">50+</div>
                  <div className="text-gray-300 text-xs">Craftsmen</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-amber-400 mb-1">2014</div>
                  <div className="text-gray-300 text-xs">Founded</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white/95 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                A Decade of <span className="text-amber-600">Creative Development</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-amber-500 p-3 rounded-full mr-4">
                      <Home className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">The Beginning</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Aaron Hands bought his first condo in 2014, marking the beginning of an incredible journey. 
                    What started as a personal investment became a passion for transforming properties across San Diego County.
                  </p>
                  <div className="flex items-center text-amber-600">
                    <Star className="w-5 h-5 mr-2" />
                    <span className="font-semibold">First Property - 2014</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="bg-orange-500 p-3 rounded-full mr-4">
                      <Hammer className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Creative Innovation</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Aaron's journey showcases creative style, innovative texture, and stunning designs. 
                    Each project reflects his vision for modern living spaces that blend functionality with aesthetic excellence.
                  </p>
                  <div className="flex items-center text-orange-600">
                    <Award className="w-5 h-5 mr-2" />
                    <span className="font-semibold">Creative Excellence</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Preview Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Featured <span className="text-gradient bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">Portfolio</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore Aaron's artistic residential designs across San Diego County. 
              Each property showcases innovative style, creative textures, and meticulous attention to detail.
            </p>
          </div>

          {/* Enhanced Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
            {featuredGalleryItems.slice(0, 8).map((item, index) => (
              <div
                key={item.id}
                className="group relative cursor-pointer transform transition-all duration-500 hover:-translate-y-2"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => setSelectedImage(item)}
              >
                <div className="relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-amber-100">
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={item.src}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    
                    {/* Enhanced Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                      <div className="transform scale-75 group-hover:scale-100 transition-all duration-300">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-xl">
                          <Eye className="w-5 h-5 text-amber-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <Button 
              onClick={() => setActiveTab("gallery")}
              size="lg" 
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-4 text-lg font-medium rounded-full shadow-2xl hover:scale-105 transition-all duration-300"
            >
              <Eye className="w-5 h-5 mr-2" />
              View Complete Portfolio
            </Button>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-amber-600">Mission</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 leading-relaxed mb-12">
              To transform ordinary spaces into extraordinary living experiences while supporting our local community. 
              Every project is an opportunity to create beauty, functionality, and lasting value for homeowners and neighborhoods.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-amber-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Home className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Craftsmanship</h3>
                  <p className="text-gray-600">Every detail matters. We ensure exceptional quality in every project.</p>
                </CardContent>
              </Card>
              
              <Card className="border-orange-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Community First</h3>
                  <p className="text-gray-600">Supporting local craftsmen and contributing to our community's growth.</p>
                </CardContent>
              </Card>
              
              <Card className="border-red-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Design Excellence</h3>
                  <p className="text-gray-600">Innovative designs that blend creativity with functionality.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* Gallery Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm">
          {/* Close Button */}
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
            <Button
              onClick={() => setSelectedImage(null)}
              variant="ghost"
              className="p-2 sm:p-3 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-200 text-white hover:text-white"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
          
          {/* Media Content */}
          <div className="max-w-full max-h-full w-full flex items-center justify-center px-2 sm:px-4">
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] sm:max-h-[90vh] object-contain rounded-lg sm:rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
